import React from 'react';
import { t } from '@lingui/macro';
import { FormTemplate } from '.';
import { CalendarIcon, FamilyIcon } from 'components/icons';
import { createIcon } from 'components/icons/createIcon';
import { light } from '@fortawesome/fontawesome-svg-core/import.macro';

const EVENT_RSVP_TEMPLATE = (): FormTemplate => ({
  title: t`Event RSVP template`,
  icon: <CalendarIcon />,
  questions: [
    {
      text: t`Name`,
      stringDefinition: {},
    },
    {
      text: t`Email`,
      stringDefinition: {},
    },
    {
      text: t`Will you attend?`,
      checkboxesDefinition: {
        options: [{ text: t`Yes` }, { text: t`No` }],
      },
    },
    {
      text: t`Number of guests`,
      numericDefinition: {
        minValue: 0,
        maxValue: 10,
      },
    },
    {
      text: t`Any food preferences we should take into account?`,
      stringDefinition: {},
    },
  ],
});

const HOW_SATISFIED_OPTIONS = () => ({
  checkboxesDefinition: {
    options: [
      { text: t`Not at all likely` },
      { text: t`Not so likely` },
      { text: t`Somewhat likely` },
      { text: t`Very likely` },
      { text: t`Extremely likely` },
    ],
  },
});

const FAMILY_SATISFACTION_SURVEY = (): FormTemplate => ({
  title: t`Short family satisfaction survey`,
  icon: <FamilyIcon />,
  questions: [
    {
      text: t`How satisfied are you with the work the family office has been doing?`,
      required: true,
      ...HOW_SATISFIED_OPTIONS(),
    },
    {
      text: t`Do you have any thoughts on how we could improve the family office's services?`,
      stringDefinition: {},
    },
  ],
});

const BriefcaseIcon = createIcon(light('briefcase'));

const SATISFACTION_SURVEY = (): FormTemplate => ({
  title: t`Platform satisfaction survey`,
  icon: <BriefcaseIcon />,
  questions: [
    {
      text: t`How likely is it that you would recommend this platform to a friend or family member?`,
      required: true,
      checkboxesDefinition: {
        options: [
          { text: t`Not at all likely` },
          { text: t`Not so likely` },
          { text: t`Somewhat likely` },
          { text: t`Very likely` },
          { text: t`Extremely likely` },
        ],
      },
    },
    {
      text: t`How satisfied are you with the reliability?`,
      required: true,
      ...HOW_SATISFIED_OPTIONS(),
    },
    {
      text: t`How satisfied are you with the security?`,
      required: true,
      ...HOW_SATISFIED_OPTIONS(),
    },
    {
      text: t`How satisfied are you with the ability to integrate with other software?`,
      required: true,
      ...HOW_SATISFIED_OPTIONS(),
    },
    {
      text: t`How satisfied are you with the ease of use?`,
      required: true,
      ...HOW_SATISFIED_OPTIONS(),
    },
    {
      text: t`How satisfied are you with the look and feel?`,
      required: true,
      ...HOW_SATISFIED_OPTIONS(),
    },
    {
      text: t`How satisfied are you with the onboarding experience?`,
      required: true,
      ...HOW_SATISFIED_OPTIONS(),
    },
    {
      text: t`How satisfied are you with the ability to collaborate with other users through the platform?`,
      required: true,
      ...HOW_SATISFIED_OPTIONS(),
    },
    {
      text: t`Do you have any thoughts on how to improve this platform?`,
      stringDefinition: {},
    },
  ],
});

const FAM_VALUES_SURVEY = (): FormTemplate => ({
  title: t`Family Mission, Vision & Values Survey`,
  icon: <FamilyIcon />,
  questions: [
    {
      text: t`Your Name`,
      required: true,
      stringDefinition: {},
    },
    {
      text: t`Role in Family (ok to select more than one)`,
      required: false,
      checkboxesDefinition: {
        options: [
          {
            text: t`Family Member (descendant)`,
          },
          {
            text: t`Married-in`,
          },
          {
            text: t`Shareholder / Owner`,
          },
          {
            text: t`Next Generation Member`,
          },
          {
            text: t`Family Council`,
          },
          {
            text: t`Board Member`,
          },
          {
            text: t`Advisor / External`,
          },
        ],
      },
    },
    {
      text: t`MISSION - Legacy: What was the purpose of the first generation in founding our family enterprise?`,
      required: true,
      stringDefinition: {},
    },
    {
      text: t`MISSION - Today: What have been the goals of my generation in continuing and growing our family enterprise?`,
      required: true,
      stringDefinition: {},
    },
    {
      text: t`MISSION - What would be missing if you sold all of your assets and became a wealthy individual rather than being a family in business all together?`,
      required: false,
      stringDefinition: {},
    },
    {
      text: t`VISION - How do you see the family coming together in the future? Or not?`,
      required: false,
      stringDefinition: {},
    },
    {
      text: t`VISION - Finish this sentence: “In five years, I want my family business(es) to be….”`,
      required: true,
      stringDefinition: {},
    },
    {
      text: t`VISION - List three or four goals you would like the business to accomplish in the next five years (these goals can be business and/or family-oriented).`,
      required: false,
      stringDefinition: {},
    },
    {
      text: t`VALUES - What are your own personal “core” values - what values or philosophies have guided your life?`,
      required: false,
      stringDefinition: {},
    },
    {
      text: t`VALUES - What were the core values of the family you grew up in? What philosophies did your parents stress when you were growing up?`,
      required: false,
      stringDefinition: {},
    },
    {
      text: t`VALUES - What are the values that you feel should guide how your family acts as owners of your family enterprise, regardless of what changes might take place to that enterprise?`,
      required: true,
      stringDefinition: {},
    },
    {
      text: t`Any additional remarks or thoughts?`,
      required: false,
      stringDefinition: {},
    },
  ],
});

export const TEMPLATE_DATABASE: (() => FormTemplate)[] = [
  EVENT_RSVP_TEMPLATE,
  FAMILY_SATISFACTION_SURVEY,
  SATISFACTION_SURVEY,
  FAM_VALUES_SURVEY,
];
